import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, Database, ChevronLeft } from "lucide-react";

/**
 * Privacy Policy — public, members + clubs read this before signing up to
 * understand exactly what we collect, how it's siloed by tenant, and what
 * (a few) things we never sell.
 *
 * Written in plain English on purpose; the legalese version lives in the
 * onboarding agreement.
 */
export default function PrivacyPolicyPage() {
  return (
    <div className="bg-cream min-h-screen" data-testid="privacy-page">
      <header className="bg-[#0f1a14] text-[#F9F8F4] border-b border-[#1A422D]">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 py-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ShieldCheck size={22} className="text-[#D9C68A]" />
            <div>
              <div className="overline" style={{ color: "#D9C68A" }}>My Crest Club</div>
              <div className="font-serif text-2xl mt-0.5">Privacy Policy</div>
            </div>
          </div>
          <Link
            to="/login"
            data-testid="privacy-back"
            className="inline-flex items-center gap-2 border border-[#D9C68A]/50 text-[#D9C68A] px-3 py-1.5 text-xs uppercase tracking-[0.22em] hover:bg-[#1A422D] transition-colors"
          >
            <ChevronLeft size={14} /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-10 space-y-10">
        <p className="text-sm text-[#7A7A7A]">Effective February 1, 2026</p>

        <section data-testid="privacy-overview" className="space-y-4">
          <h2 className="font-serif text-3xl text-[#1A1A1A]">The short version</h2>
          <ul className="space-y-3 text-[#1A1A1A] leading-relaxed list-disc pl-5">
            <li>Your account information and order history belong to you and your club. We don&rsquo;t sell, lease, or share it with anyone.</li>
            <li>Every record is tagged with your club&rsquo;s tenant ID and isolated at the database layer. Members from another club cannot see yours; staff from another club cannot reach yours.</li>
            <li>Your password is hashed with bcrypt before it ever touches the database. Even we cannot read it.</li>
            <li>Photos you upload are private to you and your club&rsquo;s pro shop. They are never indexed or exposed by a public URL.</li>
            <li>Email communication with your pro shop is end-to-end inside Resend &mdash; not stored on a third-party marketing platform.</li>
            <li>You can request a full export or deletion of your data at any time by emailing your club&rsquo;s pro shop.</li>
          </ul>
        </section>

        <section data-testid="privacy-isolation" className="space-y-4">
          <div className="flex items-center gap-2"><Database size={18} className="text-[#1A422D]" /><h3 className="font-serif text-2xl text-[#1A1A1A]">Tenant isolation</h3></div>
          <p className="text-[#1A1A1A] leading-relaxed">
            My Crest Club is a multi-tenant platform: many clubs operate on the
            same backend infrastructure, but their data is segregated by a
            cryptographic <span className="font-mono text-sm">club_id</span> stamp.
            Every member document, every order, every wishlist entry, and every
            uploaded photo carries that stamp. Every server-side query enforces
            the stamp before it returns a single byte to the requester. A
            tenant breach would require a deliberate code-level bypass &mdash; not
            a misconfiguration.
          </p>
          <p className="text-[#1A1A1A] leading-relaxed">
            Members who belong to multiple clubs have a separate active-session
            context per club. Switching clubs in the navigation reissues the
            tenant stamp; you never see your other club&rsquo;s records while
            sessioned into one. Pro shop staff are tenant-bound by their
            credentials and cannot enumerate, read, or modify records outside
            their own club&rsquo;s scope.
          </p>
        </section>

        <section data-testid="privacy-collection" className="space-y-4">
          <div className="flex items-center gap-2"><Lock size={18} className="text-[#1A422D]" /><h3 className="font-serif text-2xl text-[#1A1A1A]">What we collect</h3></div>
          <p className="text-[#1A1A1A] leading-relaxed">
            We collect only what your pro shop needs to fulfill your special
            orders: your name and contact details, your club affiliation,
            your sizing preferences, your style preferences, and the orders /
            wishlist items you submit. We never collect payment-card data;
            transactions are settled by your club at the counter or via your
            existing club account.
          </p>
        </section>

        <section data-testid="privacy-thirdparty" className="space-y-4">
          <h3 className="font-serif text-2xl text-[#1A1A1A]">Third-party services</h3>
          <p className="text-[#1A1A1A] leading-relaxed">
            We use Resend for transactional email, MongoDB Atlas for encrypted
            data storage, and a CDN for static asset delivery. No analytics,
            advertising, or marketing-automation provider has access to your
            account data.
          </p>
        </section>

        <section data-testid="privacy-control" className="space-y-4">
          <h3 className="font-serif text-2xl text-[#1A1A1A]">Your controls</h3>
          <ul className="space-y-3 text-[#1A1A1A] leading-relaxed list-disc pl-5">
            <li>Update any profile field at any time from your member dashboard.</li>
            <li>Withdraw a wishlist item or a not-yet-submitted order yourself.</li>
            <li>Request a data export or a full account deletion from your club&rsquo;s pro shop &mdash; we honor it within 14 days.</li>
            <li>Switch clubs without losing the data you&rsquo;ve built up in either.</li>
          </ul>
        </section>

        <section data-testid="privacy-contact" className="border-t border-[#E5E3D8] pt-8 space-y-2">
          <h3 className="font-serif text-2xl text-[#1A1A1A]">Questions</h3>
          <p className="text-[#1A1A1A] leading-relaxed">
            Email <a className="underline text-[#1A422D]" href="mailto:theshop@mycrestclub.com">theshop@mycrestclub.com</a> or your club&rsquo;s pro shop.
          </p>
        </section>
      </main>

      <footer className="max-w-3xl mx-auto px-6 sm:px-10 py-6 text-xs text-[#7A7A7A] flex flex-wrap gap-4">
        <Link to="/login" className="hover:text-[#1A422D]">Sign in</Link>
        <Link to="/terms" className="hover:text-[#1A422D]" data-testid="footer-terms">Terms of Service</Link>
        <span>&copy; My Crest Club</span>
      </footer>
    </div>
  );
}
